import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { adminUserService } from '../../../utils/adminUserService';

const AdminManagementSection = ({ onError, onSuccess }) => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadAdminUsers = async () => {
    setLoading(true);
    try {
      const result = await adminUserService.getAdminUsers();
      
      if (result.error) {
        throw new Error(result.error);
      }

      setAdminUsers(result.data);
    } catch (err) {
      onError(err.message);
      console.error('Error loading admin users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdminUsers();
  }, []);

  const handlePromoteUser = async (email) => {
    setActionLoading(true);
    try {
      // First find the user by email
      const usersResult = await adminUserService.getUsers({ search: email });
      
      if (usersResult.error) {
        throw new Error(usersResult.error);
      }

      const user = usersResult.data.find(u => u.email.toLowerCase() === email.toLowerCase());
      
      if (!user) {
        throw new Error('User not found');
      }

      if (user.role === 'admin') {
        throw new Error('User is already an admin');
      }

      const result = await adminUserService.promoteToAdmin(user.id);
      
      if (result.error) {
        throw new Error(result.error);
      }

      await loadAdminUsers();
      onSuccess('User promoted to admin successfully');
    } catch (err) {
      onError(err.message);
      console.error('Error promoting user:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDemoteAdmin = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to demote ${userName} from admin role?`)) {
      return;
    }

    setActionLoading(true);
    try {
      const result = await adminUserService.demoteFromAdmin(userId);
      
      if (result.error) {
        throw new Error(result.error);
      }

      await loadAdminUsers();
      onSuccess('Admin demoted successfully');
    } catch (err) {
      onError(err.message);
      console.error('Error demoting admin:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const AddAdminModal = ({ onClose }) => {
    const [email, setEmail] = useState('');
    const [promoting, setPromoting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!email.trim()) return;

      setPromoting(true);
      try {
        await handlePromoteUser(email.trim());
        onClose();
        setEmail('');
      } catch (err) {
        // Error is already handled in handlePromoteUser
      } finally {
        setPromoting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Promote User to Admin
            </h3>
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="User Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter user email address"
              required
            />
            
            <div className="text-sm text-muted-foreground">
              Enter the email address of an existing user to promote them to admin role.
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={onClose} disabled={promoting}>
                Cancel
              </Button>
              <Button type="submit" disabled={promoting || !email.trim()}>
                {promoting ? 'Promoting...' : 'Promote to Admin'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Admin Management
          </h2>
          <p className="text-muted-foreground">
            Manage administrator accounts and permissions
          </p>
        </div>
        
        <Button 
          variant="default" 
          size="sm" 
          iconName="UserPlus"
          onClick={() => setShowAddAdminModal(true)}
          disabled={actionLoading}
        >
          Promote User
        </Button>
      </div>

      {/* Admin Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Admins</p>
              <p className="text-2xl font-bold text-foreground">{adminUsers.length}</p>
            </div>
            <Icon name="Shield" size={20} className="text-red-500" />
          </div>
        </div>
        
        <div className="bg-muted/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Today</p>
              <p className="text-2xl font-bold text-foreground">
                {adminUsers.filter(admin => {
                  const lastUpdate = new Date(admin.updated_at);
                  const today = new Date();
                  return lastUpdate.toDateString() === today.toDateString();
                }).length}
              </p>
            </div>
            <Icon name="Activity" size={20} className="text-green-500" />
          </div>
        </div>
        
        <div className="bg-muted/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Recent Joins</p>
              <p className="text-2xl font-bold text-foreground">
                {adminUsers.filter(admin => {
                  const created = new Date(admin.created_at);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return created > weekAgo;
                }).length}
              </p>
            </div>
            <Icon name="TrendingUp" size={20} className="text-blue-500" />
          </div>
        </div>
      </div>

      {/* Admin Users List */}
      <div className="space-y-3">
        <h3 className="text-lg font-medium text-foreground">Administrator Accounts</h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={20} className="animate-spin mr-2" />
            <span className="text-muted-foreground">Loading admin users...</span>
          </div>
        ) : adminUsers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No admin users found
          </div>
        ) : (
          <div className="space-y-2">
            {adminUsers.map((admin) => (
              <div key={admin.id} className="flex items-center justify-between p-4 bg-muted/10 rounded-lg border border-border/50">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                    <Icon name="Shield" size={16} className="text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{admin.full_name}</p>
                    <p className="text-sm text-muted-foreground">{admin.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm text-foreground">
                      Joined {formatDate(admin.created_at)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Last active {formatDate(admin.updated_at)}
                    </p>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="UserMinus"
                    onClick={() => handleDemoteAdmin(admin.id, admin.full_name)}
                    disabled={actionLoading}
                    title="Demote from Admin"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin Permissions Info */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
              Admin Permissions
            </h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Administrators have full access to all platform features including user management, 
              content moderation, system settings, and analytics. Use caution when promoting users 
              to admin role.
            </p>
          </div>
        </div>
      </div>

      {showAddAdminModal && (
        <AddAdminModal onClose={() => setShowAddAdminModal(false)} />
      )}
    </div>
  );
};

export default AdminManagementSection;
